import { Maths } from 'helpers/maths';

/**
 * Calculate applied acre percentage based on applied acres and total area
 * @param appliedAcres - The number of acres being applied to
 * @param totalArea - The total area available
 * @returns The percentage of total area being applied to (0-100)
 */
export const calculateAppliedAcrePercent = (
  appliedAcres: number,
  totalArea: number
): number => {
  if (totalArea <= 0 || appliedAcres < 0) {
    return 0;
  }

  const percentage = Maths.multiplyRound(
    Maths.divideSafeRound(appliedAcres, totalArea),
    100
  );

  return Math.min(percentage, 100);
};

/**
 * Calculate applied acres based on percentage and total area
 * @param percentage - The percentage of total area to apply to (0-100)
 * @param totalArea - The total area available
 * @returns The number of acres to apply to
 */
export const calculateAppliedAcres = (
  percentage: number,
  totalArea: number
): number => {
  if (totalArea <= 0 || percentage < 0 || percentage > 100) {
    return 0;
  }

  return Maths.divideSafeRound(Maths.multiplyRound(totalArea, percentage), 100);
};

/**
 * Validate that applied acres value is within acceptable range
 * @param appliedAcres - The applied acres value to validate
 * @param totalArea - The total area available
 * @returns True if valid, false otherwise
 */
export const validateAppliedAcres = (
  appliedAcres: number,
  totalArea: number
): boolean => {
  return appliedAcres >= 0 && appliedAcres <= totalArea;
};

/**
 * Validate that applied percentage value is within acceptable range
 * @param percentage - The percentage value to validate
 * @returns True if valid (0-100), false otherwise
 */
export const validateAppliedPercent = (percentage: number): boolean => {
  return percentage > 0 && percentage <= 100;
};
